import { useEffect, useState } from "react";
import type { ShiftDTO, StaffMemberDTO } from "@easyshop/shared";
import { Button, Drawer, DrawerHeader, ErrorState, FieldLabel, inputClass } from "@/components/ui";
import { api } from "@/lib/api";
import { useInvalidatePlanning } from "@/lib/queries";
import { dayLong } from "./utils";

export interface ShiftDraft {
  shift?: ShiftDTO;
  boutiqueId: string;
  staffMemberId?: string;
  date: string;
}

const PRESETS = [
  { label: "Ouverture", start: "09:00", end: "14:00", pause: 0 },
  { label: "Fermeture", start: "14:00", end: "20:00", pause: 0 },
  { label: "Journée", start: "10:00", end: "18:00", pause: 60 },
];

/** Créer / modifier / supprimer un créneau. Les conflits (chevauchement, absence) viennent de l'API. */
export function ShiftDrawer({ draft, staff, onClose }: { draft: ShiftDraft | null; staff: StaffMemberDTO[]; onClose: () => void }) {
  const invalidate = useInvalidatePlanning();
  const [staffMemberId, setStaff] = useState("");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("14:00");
  const [pause, setPause] = useState(0);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!draft) return;
    setError(null);
    setStaff(draft.shift?.staffMemberId ?? draft.staffMemberId ?? staff.find((s) => s.active)?.id ?? "");
    setDate(draft.shift?.date ?? draft.date);
    setStart(draft.shift?.start ?? "09:00");
    setEnd(draft.shift?.end ?? "14:00");
    setPause(draft.shift?.breakMinutes ?? 0);
    setNote(draft.shift?.note ?? "");
  }, [draft, staff]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await invalidate();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(() => {
      const body = { staffMemberId, boutiqueId: draft!.boutiqueId, date, start, end, breakMinutes: pause, note: note || null };
      return draft!.shift ? api.patch(`/shifts/${draft!.shift.id}`, body) : api.post("/shifts", body);
    });

  return (
    <Drawer open={Boolean(draft)} onClose={onClose}>
      <DrawerHeader title={draft?.shift ? "Modifier le créneau" : "Nouveau créneau"} onClose={onClose} />
      {draft && <div className="-mt-2 text-[12.5px] text-muted first-letter:uppercase">{dayLong(date || draft.date)}</div>}
      <div>
        <FieldLabel>Personne</FieldLabel>
        <select className={inputClass} value={staffMemberId} onChange={(e) => setStaff(e.target.value)}>
          {staff
            .filter((s) => s.active || s.id === staffMemberId)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.jobTitle ? ` — ${s.jobTitle}` : ""}
              </option>
            ))}
        </select>
      </div>
      <div>
        <FieldLabel>Date</FieldLabel>
        <input type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div>
        <FieldLabel>Horaires</FieldLabel>
        <div className="mb-2 flex flex-wrap gap-[6px]">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                setStart(p.start);
                setEnd(p.end);
                setPause(p.pause);
              }}
              className="rounded-full border border-line bg-white px-3 py-[6px] text-[11.5px] font-semibold text-muted hover:text-ink"
            >
              {p.label} {p.start}–{p.end}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input type="time" className={inputClass} value={start} onChange={(e) => setStart(e.target.value)} />
          <span className="text-muted">→</span>
          <input type="time" className={inputClass} value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>
      <div>
        <FieldLabel>Pause (minutes)</FieldLabel>
        <input type="number" min={0} max={240} step={15} className={inputClass} value={pause} onChange={(e) => setPause(Number(e.target.value))} />
      </div>
      <div>
        <FieldLabel>Note</FieldLabel>
        <input className={inputClass} value={note} placeholder="Ex. inventaire, mise en place promo…" onChange={(e) => setNote(e.target.value)} />
      </div>
      {error && <ErrorState error={new Error(error)} />}
      <div className="flex-1" />
      <div className="flex gap-[10px]">
        {draft?.shift ? (
          <Button
            variant="outline"
            className="flex-1 text-danger"
            disabled={busy}
            onClick={() => window.confirm("Supprimer ce créneau ?") && void run(() => api.del(`/shifts/${draft.shift!.id}`))}
          >
            Supprimer
          </Button>
        ) : (
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Annuler
          </Button>
        )}
        <Button className="flex-1" disabled={busy || !staffMemberId || !date} onClick={() => void save()}>
          Enregistrer
        </Button>
      </div>
    </Drawer>
  );
}
